/* eslint-disable react/no-children-prop */
import { ArrowUpDownIcon, DownloadIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useReactToPrint } from 'react-to-print';

import { exportExcel } from '../../../utils/excelexport';
import { PaginationSquare } from '../pagination/PaginationSquare';

type TableAccumuledValueProps = {
  dataTable: {
    nome_produto: string;
    nome_cliente: string;
    valor_premio: string;
    periodicidade_pagamento: string;
    numero_proposta: number;
    meio_pagamento: string;
    created_at: string;
    situacao: string;
  }[];
  current: number;
  limit: number;
  setLimit: any;
  total: number;
  offset: number;
  setOffset: any;
  quantitative: number;
  label: string;
  dataOptionsForDataExport: {
    type: string;
  }[];
  isLoading: boolean;
  handleSearch: (
    valorBusca: string,
    event: 'change' | 'blur' | 'click'
  ) => void;
  handleOrder: (campo: string, ordem: string) => void;
  searchBarValue: string;
};

export const TableAccumuledValue = ({
  current,
  limit,
  setLimit,
  total,
  offset,
  setOffset,
  dataTable,
  quantitative,
  label,
  dataOptionsForDataExport,
  isLoading,
  handleSearch,
  handleOrder,
  searchBarValue
}: TableAccumuledValueProps) => {
  const toast = useToast();

  const refPrint = useRef() as MutableRefObject<HTMLDivElement>;

  const [ordemProduto, setOrdemProduto] = useState<'asc' | 'desc' | ''>('');
  const [ordemPremio, setOrdemPremio] = useState<'asc' | 'desc' | ''>('');
  const [ordemData, setOrdemData] = useState<'asc' | 'desc' | ''>('');
  const [ordemSituacao, setOrdemSituacao] = useState<'asc' | 'desc' | ''>('');

  const [dataExport, setDataExport] = useState('');
  const [valueForSearch, setValueForSearch] = useState('');

  const indexCurrentData = () => {
    return dataTable?.slice(current * limit - limit, current * limit);
  };

  const csvData = () => {
    return dataTable.map((item) => {
      return {
        Produto: item.nome_produto,
        Cliente: item.nome_cliente,
        'Valor do Prêmio': item.valor_premio,
        Proposta: item.numero_proposta,
        'Periodicidade de Pagamento': item.periodicidade_pagamento,
        'Tipo de Cobrança': item.meio_pagamento,
        'Data ': item.created_at,
        'Situação ': item.situacao
      };
    });
  };
  const csvLink = () => {
    return {
      filename: `${label}.csv`,
      data: csvData()
    };
  };

  const handlePrint = useReactToPrint({
    content: () => {
      return refPrint.current;
    }
  });

  const handleLinkForExportData = () => {
    if (dataExport === '') {
      toast({
        position: 'top',
        description: `Selecione um tipo de arquivo`,
        status: 'error',
        duration: 10000,
        isClosable: true
      });
    }
    if (dataExport === 'pdf') {
      handlePrint();
    }

    if (dataExport === 'xlsx') {
      exportExcel({
        excelData: csvData(),
        fileName: `valores-acumulados-${new Date()
          .toISOString()
          .substring(0, 10)}`
      });
    }
  };

  useEffect(() => {
    handleOrder('produto', ordemProduto);
  }, [ordemProduto]);

  useEffect(() => {
    handleOrder('premio', ordemPremio);
  }, [ordemPremio]);

  useEffect(() => {
    handleOrder('data', ordemData);
  }, [ordemData]);

  useEffect(() => {
    handleOrder('situacao', ordemSituacao);
  }, [ordemSituacao]);

  useEffect(() => {
    handleSearch(valueForSearch, 'change');
  }, [valueForSearch]);

  useEffect(() => {
    setValueForSearch(searchBarValue);
  }, [searchBarValue]);

  const mapPeriodicidades = {
    A: 'Anual',
    S: 'Semestral',
    T: 'Trimestral',
    M: 'Mensal'
  };

  return (
    <Stack
      m="30px 0 0 0 !important"
      bg="#FFFFFF"
      p="30px"
      justifyContent="space-between"
      borderRadius="24px"
      boxShadow="0px 0px 5px rgba(50, 50, 50, 0.25)"
    >
      <Box display="flex !important" justifyContent="space-between">
        <Stack>
          <Text fontWeight="700" fontSize="1.25rem" lineHeight="2rem">
            {label}
          </Text>
          <Text
            fontWeight="400"
            fontSize="1rem"
            lineHeight="1.5rem"
            color="#001E64"
          >
            Quantidade de vendas: {quantitative}
          </Text>
        </Stack>
        <HStack gridGap="10px">
          <InputGroup w="250px">
            <InputLeftElement
              cursor="pointer"
              onClick={() => {
                handleSearch(valueForSearch, 'click');
              }}
              h="50px"
              children={<SearchIcon color="#001E64 " />}
            />
            <Input
              w="250px"
              h="50px"
              value={valueForSearch}
              border="1px solid #001E64 !important"
              borderRadius="133px"
              placeholder="Busca por (produto, nome, proposta ou situação)"
              onChange={(e) => {
                setValueForSearch(e.target.value);
              }}
              onBlur={(e) => {
                handleSearch(e.target.value, 'blur');
              }}
              _hover={{}}
              _active={{}}
              _focus={{
                '&:focus': {
                  boxShadow: 'none !important'
                }
              }}
            />
          </InputGroup>
          <Select
            w="250px"
            h="50px"
            value={dataExport}
            color={dataExport === '' ? '#828282' : '#001E64'}
            fontWeight={dataExport === '' ? '400' : '700'}
            border="1px solid #001E64 !important"
            borderRadius="133px"
            placeholder="Selecione um tipo de arquivo"
            onChange={(e) => {
              setDataExport(e.target.value);
            }}
            _hover={{}}
            _active={{}}
            _focus={{
              '&:focus': {
                boxShadow: 'none !important'
              }
            }}
          >
            {dataOptionsForDataExport.map((item, index) => {
              return (
                <option key={index} value={item.type}>
                  {item.type}
                </option>
              );
            })}
          </Select>
          {dataExport === 'csv' ? (
            <CSVLink {...csvLink()} separator={';'}>
              <Tooltip
                label="Exportar dados"
                bg="gray.200"
                placement="top-end"
                color="#323232"
              >
                <DownloadIcon
                  m="0 !important"
                  w="18px"
                  h="18px"
                  color="#001E64"
                />
              </Tooltip>
            </CSVLink>
          ) : (
            <Box>
              <Tooltip
                label="Exportar dados"
                bg="gray.200"
                placement="top-end"
                color="#323232"
              >
                <DownloadIcon
                  m="0 !important"
                  w="18px"
                  h="18px"
                  color="#001E64"
                  onClick={() => {
                    handleLinkForExportData();
                  }}
                />
              </Tooltip>
            </Box>
          )}
        </HStack>
      </Box>
      {isLoading ? (
        <Box
          w="100%"
          h="55vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gridGap="15px"
        >
          <Spinner
            thickness="6px"
            speed="0.65s"
            emptyColor="#FF5000"
            color="#FFFFFF"
            size="xl"
          />
          <Text>Carregando Valores Acumulados...</Text>
        </Box>
      ) : (
        <TableContainer
          ref={refPrint}
          m="20px 0 0 0 !important"
          w="100%"
          maxH="170vh"
          overflowY="auto"
        >
          <Table
            variant="striped"
            colorScheme="blackAlpha"
            size="sm"
            position="relative"
            style={{ borderCollapse: 'separate', borderSpacing: '0 10px' }}
          >
            <Thead
              h="3.25rem"
              z-index={9}
              position="sticky"
              top={0}
              zIndex={999}
              bg="#FFFFFF"
            >
              <Tr>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Produto
                  <ArrowUpDownIcon
                    m="0 0 0 5px !important"
                    onClick={() => {
                      setOrdemProduto(ordemProduto !== 'desc' ? 'desc' : 'asc');
                    }}
                    fontSize="14px"
                    cursor="pointer"
                  />
                </Th>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Cliente
                </Th>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Valor <br /> do Prêmio
                  <ArrowUpDownIcon
                    m="0 0 0 5px !important"
                    onClick={() => {
                      setOrdemPremio(ordemPremio !== 'desc' ? 'desc' : 'asc');
                    }}
                    fontSize="14px"
                    cursor="pointer"
                  />
                </Th>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Proposta
                </Th>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Periodiciodade
                  <br /> de Pagamento
                </Th>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Tipo <br /> de Cobrança
                </Th>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Data
                  <ArrowUpDownIcon
                    m="0 0 0 5px !important"
                    onClick={() => {
                      setOrdemData(ordemData !== 'desc' ? 'desc' : 'asc');
                    }}
                    fontSize="14px"
                    cursor="pointer"
                  />
                </Th>
                <Th
                  fontSize="0.75rem"
                  fontWeight="700"
                  lineHeight="1.5rem"
                  color="#001E64"
                  p="20px 0 15px 15px !important"
                  textAlign="center"
                >
                  Situação
                  <ArrowUpDownIcon
                    m="0 0 0 5px !important"
                    onClick={() => {
                      setOrdemSituacao(
                        ordemSituacao !== 'desc' ? 'desc' : 'asc'
                      );
                    }}
                    fontSize="14px"
                    cursor="pointer"
                  />
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {Array.isArray(indexCurrentData())
                ? indexCurrentData()?.map((item: any, index: any) => {
                    const date = new Date(item.created_at);

                    const year = date.getFullYear();
                    const month = date.getMonth() + 1;
                    const day = date.getDate();

                    const newDate = `${day < 10 ? `0${day}` : day}/${
                      month < 10 ? `0${month}` : month
                    }/${year}`;
                    return (
                      <Tr key={index}>
                        <Td
                          display="flex"
                          alignItems="center"
                          gridGap="10px"
                          position="relative"
                          p="16px 0 16px 15px !important"
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          <Box
                            w="7px"
                            h="73.24px"
                            bg={
                              item.situacao === 'ATIVA'
                                ? '#001E64'
                                : item.situacao === 'Recusada'
                                ? '#AA2C23'
                                : ' #FF5000'
                            }
                            borderRadius="24px 0 0 24px"
                            position="absolute"
                            left="0"
                          >
                            <span style={{ visibility: 'hidden' }}>tag</span>
                          </Box>
                          <Box p="10px">{item.nome_produto}</Box>
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          {item.nome_cliente}
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#006217"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="700"
                        >
                          R$ &nbsp;
                          {Number(
                            isNaN(item.valor_premio) ? 0 : item.valor_premio
                          )
                            .toFixed(2)
                            .replace('.', ',')}
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="700"
                        >
                          {item.numero_proposta}
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                          textAlign="center"
                        >
                          {mapPeriodicidades[item.periodicidade_pagamento]}
                        </Td>
                        <Td
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#001E64"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          {item.meio_pagamento}
                        </Td>
                        <Td
                          w="100px !important"
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color=" #146CFA"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="700"
                        >
                          {newDate}
                        </Td>
                        <Td
                          w="100px !important"
                          border="none"
                          bg="rgba(211, 222, 235, .5)"
                          color="#FFFFFF"
                          fontSize="0.875rem"
                          lineHeight="1.5rem"
                          fontWeight="400"
                        >
                          <Box
                            bg={
                              item.situacao === 'ACEITA'
                                ? '#001E64'
                                : item.situacao === 'RECUSADA'
                                ? '#AA2C23'
                                : item.situacao === 'FINALIZADA'
                                ? ' #FF5000'
                                : '#033001'
                            }
                            textAlign="center"
                            p="6px 10px"
                            borderRadius="7px"
                          >
                            {item.situacao
                              ? (
                                  item.situacao[0].toUpperCase() +
                                  item.situacao.slice(1).toLowerCase()
                                ).replaceAll('_', ' ')
                              : ''}
                          </Box>
                        </Td>
                      </Tr>
                    );
                  })
                : ''}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <PaginationSquare
        offset={offset}
        limit={limit}
        total={total}
        setOffset={setOffset}
        setLimit={setLimit}
      />
    </Stack>
  );
};
